'use strict';

import MixpanelService from '../mixpanel/mixpanel.service';
import Queue from '../../model/domain/queue';
import { OrganizationTypes } from '../../model/domain/organization';
import { AuthType } from '../auth/auth.service';
import { Locations } from '../mixpanel/mixpanel.service';

export default class AnalyticsService {
  constructor($log, $q, MixpanelService, LogRocketService) {
    'ngInject';

    this.$log = $log;
    this.$q = $q;

    /** @type {Deferred} */
    this._whenReadyDeferred = null;
    /** @type {Promise.<UserTokenInfo>} */
    this._whenReady = null;
    /** @type {MixpanelService} */
    this._mixpanelService = MixpanelService;
    /** @type {LogRocketService} */
    this._logRocketService = LogRocketService;
    /** @type <UserTokenInfo> */
    this._userData = null;

    this.logQueue = new Queue();
    this.isLoggedIn = false;
    this.isTeacher = false;
  }

  _addToAnalyticsQueue(eventInfo) {
    this.logQueue.enqueue(eventInfo);
  }

  _flushQueue() {
    while (this.logQueue.length > 0) {
      const firstItem = this.logQueue.dequeue();
      this._sendToMixpanel(firstItem);
    }
  }

  _sendToMixpanel(eventInfo) {
    const tag = eventInfo && eventInfo.eventTag;
    const properties = eventInfo && eventInfo.properties;
    const count = eventInfo && eventInfo.count;

    this._mixpanelService.send(tag, properties, count);
  }

  _activateMixpanelService(userData, abTestSegments) {
    this._mixpanelService.init(userData, abTestSegments);
  }

  identifyUserAsLoggedIn(userData, abTestSegments) {
    this._userData = userData;

    if (this._whenReadyDeferred) {
      this._whenReadyDeferred.resolve(this._userData);
      this._whenReadyDeferred = null;
    } else {
      this._whenReady = this.$q.resolve(this._userData);
    }

    if (userData.isTeacher) {
      this._activateMixpanelService(userData, abTestSegments);
      this.isTeacher = true;
      this.isLoggedIn = true;
      this._flushQueue();
      if (!this._logRocketService.initialized) {
        this._logRocketService.identify(
          userData.id,
          userData.name,
          userData.email,
          userData.username,
          userData.isPro,
        );
      }
    } else {
      this.logQueue = new Queue();
    }
  }

  clearOutData() {
    this.isLoggedIn = false;
    this.isTeacher = false;
    this.logQueue = new Queue();
    this._mixpanelService.identify();
  }

  sendEvent(eventInfo) {
    if (!this.isLoggedIn && !this.isTeacher) {
      this._addToAnalyticsQueue(eventInfo);
    } else {
      this._flushQueue();
      this._sendToMixpanel(eventInfo);
    }
  }

  /**
   * Moved this over from the Mixpanel. It makes analytics wait to be logged until after
   * identify is called. Helps to address out of order logging
   * due to retrieving superproperties from the server
   */
  lockUntilIdentify() {
    if (!this._whenReadyDeferred) {
      this._whenReadyDeferred = this.$q.defer();
    }
    this._whenReady = this._whenReadyDeferred.promise;
  }

  setMixpanelProperty(property, value) {
    this._mixpanelService.setProperty(property, value);
  }

  /******** Mixpanel Events that have been moved to Analytics Page ********/

  /**
   * Teacher creates an assignment
   *
   * @param assignmentId {string}
   */
  assignmentCreated(assignmentId) {
    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: 'T added Assignment',
        properties: {
          assignmentId: assignmentId,
        },
        count: true,
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send(
      'T added Assignment',
      {
        assignmentId: assignmentId,
      },
      true,
    );
  }

  /**
   * Published after each step in the account creation process
   *
   * @param step {string} started | name | location | referral | complete | schoolName
   */
  createAccountProgress(step) {
    this._whenReady.then((userData) => {
      if (!this.isLoggedIn && !this.isTeacher) {
        const eventInfo = {
          eventTag: 'createAccountProgress',
          properties: {
            completedStep: step,
            userType: userData.roles[0],
          },
        };
        return this.sendEvent(eventInfo);
      }

      this._mixpanelService.send('createAccountProgress', {
        completedStep: step,
        userType: this._userData.roles[0],
      });
    });
  }

  /**
   * When a teacher opens the share assignment dialog, and clicks on the options. started = opened dialog, the rest pertain to specific buttons
   *
   * @param assignmentId {string}
   * @param location {string} assignmentEdit | assignmentList
   * @param step {string} started | withColleagues | withStudents | viaEmail | viaLink
   */
  shareAssignmentWorkflow(
    assignmentId,
    isFolder,
    belongsToFolder,
    location,
    step,
  ) {
    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: 'shareAssignmentWorkflow',
        properties: {
          assignmentId: assignmentId,
          isFolder: isFolder,
          belongsToFolder: belongsToFolder,
          location: location,
          step: step,
        },
        count: true,
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send(
      'shareAssignmentWorkflow',
      {
        assignmentId: assignmentId,
        isFolder: isFolder,
        belongsToFolder: belongsToFolder,
        location: location,
        step: step,
      },
      true,
    );
  }

  /**
   * User clicks a button to navigate to whole class view
   *
   * @param assignmentId {string}
   * @param location {string} assignmentEdit-top | assignmentEdit-roster | assignmentList
   */
  tappedViewStudentWork(assignmentId, location) {
    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: 'Tapped ViewStudentWork',
        properties: {
          assignmentId: assignmentId,
          location: location,
        },
        count: true,
      };
      return this.sendEvent(eventInfo);
    }

    this._mixpanelService.send(
      'Tapped ViewStudentWork',
      {
        assignmentId: assignmentId,
        location: location,
      },
      true,
    );
  }

  /**
   * Incentive analytics
   *
   * @param event_name
   * @param incentive_id
   */
  incentiveSend(event_name, incentive_id) {
    this._mixpanelService.send(
      event_name,
      {
        incentiveId: incentive_id,
      },
      true,
    );
  }

  shareWorkDialogOpened(location) {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const eventInfo = {
      eventTag: `${location}:shareWorkDialog_opened`,
      properties: {
        userId,
      },
      count: true,
    };
    if (!this.isLoggedIn && !this.isTeacher) {
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      `${location}:shareWorkDialog_opened`,
      {
        ...eventInfo.properties,
      },
      true,
    );
  }

  studentJoinedWithClassCode() {
    const userId = this._userData ? this._userData.id : 'anonymous';
    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: 'studentAddedToClass_classCode',
        properties: {
          userId: userId,
        },
        count: true,
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'studentAddedToClass_classCode',
      {
        userId: userId,
      },
      true,
    );
  }
  //-------------------- Per Question Summary Methods ------------------------------//
  slidePointValueClicked() {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';
    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: 'slidePointValue_clicked',
        properties: {
          userId: userId,
          userEmail: userEmail,
        },
        count: true,
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'slidePointValue_clicked',
      {
        userId: userId,
        userEmail: userEmail,
      },
      true,
    );
  }
  averageScoreClicked() {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';
    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: 'averageScore_clicked',
        properties: {
          userId: userId,
          userEmail: userEmail,
        },
        count: true,
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'averageScore_clicked',
      {
        userId: userId,
        userEmail: userEmail,
      },
      true,
    );
  }
  percentageCorrectClicked() {
    const userId = this._userData ? this._userData.id : 'anonymous';
    const userEmail = this._userData ? this._userData.email : 'n/a';
    if (!this.isLoggedIn && !this.isTeacher) {
      const eventInfo = {
        eventTag: 'percentageCorrect_clicked',
        properties: {
          userId: userId,
          userEmail: userEmail,
        },
        count: true,
      };
      return this.sendEvent(eventInfo);
    }
    this._mixpanelService.send(
      'percentageCorrect_clicked',
      {
        userId: userId,
        userEmail: userEmail,
      },
      true,
    );
  }
}
